<template>
  <tr>
    <td>
      <wui-input
        v-model="blfModel.buttonNumber"
        type="number"
        :disabled="isOwn"
      />
    </td>
    <td>
      <wui-select
        v-model="blfModel.type"
        data-test="select"
        :class="{ '[&_.wui-select__field]:border! [&_.wui-select__field]:text-warning-shades-600': fieldsWithErrors.has('type') }"
        :size="32"
        :items="typesItems"
        :disabled="isTypeSelectDisabled"
      />
    </td>
    <td>
      <wui-input
        v-model="blfModel.phoneNumber!"
        :disabled="blfModel.type === BlfTypes.EXTENSION_MOBILITY || isOwn"
        :class="{ 'border! text-warning-shades-600': fieldsWithErrors.has('phoneNumber') }"
      />
    </td>
    <td>
      <wui-input
        v-model="blfModel.header!"
      />
    </td>
    <td
      v-if="showPage"
    >
      {{ blfModel.buttonPage }}
    </td>
    <td>
      <div class="flex items-center gap-2 pl-2">
        <wui-btn
          :disabled="!getIsValidRow"
          auto-loading
          variant="positive"
          state="alpha"
          icon
          prepend-icon="saveFloppyM"
          :size="24"
          @click="save"
        />
        <wui-btn
          v-if="!isOwn"
          data-test="remove"
          auto-loading
          icon
          variant="negative"
          prepend-icon="trashM"
          state="alpha"
          :size="24"
          @click="onRemoveClick"
        />
      </div>
    </td>
  </tr>
</template>

<script lang="ts" setup>
import { WuiInput, WuiSelect, WuiBtn, useRenderNotifier } from '@wui/common-library'
import { watch, ref, Ref, computed } from 'vue'

import { BlfDto, BlfTypes, useBlfStore } from '@/features/blf'

import { useLocalization } from '@/shared/composables/localization'

import { getAllowedBlfTypes, isConfiguredButtonsRestricted } from '../lib/get-allowed-blf-types'
import { useSettingsTerminals } from '../lib/use-settings-terminals'

const props = withDefaults(defineProps<{
  blf: BlfDto
  mac: string
  canChoiceLine?: boolean
  showPage: boolean
  isConfiguredButtonsSupported?: boolean | undefined
}>(), {
  canChoiceLine: true,
  isConfiguredButtonsSupported: undefined,
})

const { blfTypes } = useSettingsTerminals()
const { create, remove, removeLocalBlf, update } = useBlfStore()
const { notifyError, notifySuccess } = useRenderNotifier()
const { t } = useLocalization()

const blfModel = ref<BlfDto>({ ...props.blf })

const fieldsWithErrors: Ref<Set<string>> = ref(new Set<string>())

const isOwn = computed(() => blfModel.value.type === BlfTypes.LINE)
const isTypeSelectDisabled = computed(() => {
  return isOwn.value || isConfiguredButtonsRestricted(props.isConfiguredButtonsSupported)
})
const typesItems = computed(() => getAllowedBlfTypes(blfTypes.value, {
  currentType: blfModel.value.type,
  isConfiguredButtonsSupported: props.isConfiguredButtonsSupported,
}))

const getIsValidRow = () => {
  if (blfModel.value.type !== BlfTypes.NOT_USED && blfModel.value.type !== BlfTypes.EXTENSION_MOBILITY) {
    const validPhoneNumber = !!blfModel.value.phoneNumber && blfModel.value.phoneNumber.match(/^[0-9#*+w]*$/) !== null
    if (!validPhoneNumber) {
      fieldsWithErrors.value.add('phoneNumber')
    } else {
      fieldsWithErrors.value.delete('phoneNumber')
    }
    if (blfModel.value.type === BlfTypes.LINE && !props.canChoiceLine) {
      fieldsWithErrors.value.add('type')
    } else {
      fieldsWithErrors.value.delete('type')
    }
  } else {
    fieldsWithErrors.value.delete('phoneNumber')
  }
  if (isConfiguredButtonsRestricted(props.isConfiguredButtonsSupported) && blfModel.value.type !== BlfTypes.LINE) {
    fieldsWithErrors.value.add('type')
  }
  return !(fieldsWithErrors.value.size > 0)
}

const onRemoveClick = async () => {
  if (blfModel.value.id) {
    removeLocalBlf(blfModel.value.id, props.mac)
    return
  }
  if (!blfModel.value.guid) return
  await remove(blfModel.value.guid, props.mac)
}

const save = async () => {
  if (!getIsValidRow()) return
  const blf: BlfDto = { ...blfModel.value }
  if (blf.id) {
    try {
      await create(blf)
      notifySuccess(t('common.changesHasBeenSaved'))
    } catch (e: unknown) {
      const errorMessage = e instanceof Error ? e.message : t('common.unknownError')
      notifyError(errorMessage)
    }
  } else {
    try {
      await update(blf)
      notifySuccess(t('common.changesHasBeenSaved'))
    } catch (e: unknown) {
      const errorMessage = e instanceof Error ? e.message : t('common.unknownError')
      notifyError(errorMessage)
    }
  }
}

watch(() => blfModel.value.type, (v) => {
  if (v !== BlfTypes.EXTENSION_MOBILITY) {
    return
  }
  blfModel.value.phoneNumber = ''
})

</script>
